import { create } from 'zustand'
import cryptoWithdrawApi from '../services/cryptoWithdrawApi'
import useAppStore from '../../../store/useAppStore'

/**
 * Crypto Withdraw Store
 * Zustand store для управления состоянием вывода кристаллов в криптовалюте
 *
 * Поддерживает поллинг активной заявки (каждые 12 секунд),
 * пока статус не станет финальным (success / rejected)
 */

const POLLING_INTERVAL = 12000 // 12 секунд

const useCryptoWithdrawStore = create((set, get) => ({
  // Данные экрана из GET /api/crypto-withdraw/
  info: null,         // { enabled, can_withdraw, has_active, has_deposit, crystals_per_unit_net, min_crystals, max_crystals, min_amount_minor, max_amount_minor, daily_max_crystals, daily_left_crystals, assets: [...] }
  history: [],        // последние заявки

  // Состояния загрузки
  isLoading: false,
  isSubmitting: false,
  error: null,

  // ID таймера поллинга
  _pollingTimer: null,

  /**
   * Загрузить данные экрана вывода (info + history)
   */
  loadData: async () => {
    set({ isLoading: true, error: null })

    try {
      const data = await cryptoWithdrawApi.fetchCryptoWithdrawData()
      set({
        info: data.info || null,
        history: data.history || [],
        isLoading: false,
      })

      // Если есть активная заявка — запускаем поллинг
      if (data.info?.has_active) {
        get().startPolling()
      }

      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки данных вывода Crypto:', error)
      set({
        isLoading: false,
        error: error.message || 'error backend',
      })
      throw error
    }
  },

  /**
   * Создать заявку на вывод в криптовалюте
   * @param {Object} params — { asset, network, address, crystals }
   * @returns {Promise<Object>} результат { id, asset, network, amount_minor, amount, crystals, status }
   */
  submitCryptoWithdraw: async ({ asset, network, address, crystals }) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await cryptoWithdrawApi.createCryptoWithdraw({ asset, network, address, crystals })
      set({ isSubmitting: false })

      // Перезагружаем данные чтобы получить обновлённую историю и has_active
      await get().loadData()

      return result
    } catch (error) {
      console.error('❌ Ошибка создания заявки вывода Crypto:', error)
      set({
        isSubmitting: false,
        error: error.message || 'error backend',
      })
      throw error
    }
  },

  /**
   * Запустить поллинг статуса активной заявки
   */
  startPolling: () => {
    // Останавливаем предыдущий таймер если был
    get().stopPolling()

    const timer = setInterval(async () => {
      const { info } = get()

      // Если нет активной заявки — останавливаем поллинг
      if (!info?.has_active) {
        get().stopPolling()
        return
      }

      try {
        const data = await cryptoWithdrawApi.fetchCryptoWithdrawData()
        set({
          info: data.info || null,
          history: data.history || [],
        })

        // Если активная заявка перешла в финальный статус — останавливаем поллинг и обновляем баланс
        if (!data.info?.has_active) {
          get().stopPolling()
          // Обновляем баланс — при reject кристаллы возвращаются
          useAppStore.getState().loadProfile?.()
        }
      } catch (error) {
        console.error('❌ Ошибка поллинга вывода Crypto:', error)
        // При ошибке не останавливаем поллинг — попробуем снова
      }
    }, POLLING_INTERVAL)

    set({ _pollingTimer: timer })
  },

  /**
   * Остановить поллинг
   */
  stopPolling: () => {
    const { _pollingTimer } = get()
    if (_pollingTimer) {
      clearInterval(_pollingTimer)
      set({ _pollingTimer: null })
    }
  },

  /**
   * Сбросить состояние store
   */
  reset: () => {
    get().stopPolling()
    set({
      info: null,
      history: [],
      isLoading: false,
      isSubmitting: false,
      error: null,
    })
  },
}))

export default useCryptoWithdrawStore
